// react
import styled, { css } from 'styled-components';

import { FieldType } from '@karehero/models';

// components
import {
  Button,
  ButtonSocial,
  Container,
  Divider,
  Logo,
  Title,
} from '@/components/atoms';
import { EditorReveal } from '@/components/organisms';
import { HorizontalSplit } from '@/components/templates';
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';

// assets
// @transform-path ../../../../../../src/assets/ribbon2.svg
import ribbon2 from '@/assets/ribbon2.svg';

interface RegisterProps {
  onGoogleRegister?: () => void;
  onFacebookRegister?: () => void;
  onAppleRegister?: () => void;
  onEmailRegister: (email: string) => void;
}

/**
 * Register is the page where the user can sign in.
 */
export const Register = ({
  onGoogleRegister,
  onFacebookRegister,
  onAppleRegister,
  onEmailRegister,
}: RegisterProps) => {
  return (
    <HorizontalSplit
      topContent={
        <TopContent>
          <Logo size='lg' />
          <RibbonWrapper>
            <img src={ribbon2} />
          </RibbonWrapper>
        </TopContent>
      }
      bottomContent={
        <StyledContainer size='sm'>
          <Wrapper>
            <ContentWrapper>
              <StyledTitle>Complete your registration</StyledTitle>
            </ContentWrapper>
            <ButtonContainer>
              <ButtonSocialContainer>
                {onAppleRegister && (
                  <ButtonSocial
                    variant='apple'
                    ariaLabel='register with Apple'
                    onPress={onAppleRegister}
                    isFullWidth
                  >
                    Register with Apple
                  </ButtonSocial>
                )}
                {onGoogleRegister && (
                  <ButtonSocial
                    variant='google'
                    ariaLabel='register with Google'
                    onPress={onGoogleRegister}
                    isFullWidth
                  >
                    Register with Google
                  </ButtonSocial>
                )}
                {onFacebookRegister && (
                  <ButtonSocial
                    variant='facebook'
                    ariaLabel='register with Facebook'
                    onPress={onFacebookRegister}
                    isFullWidth
                  >
                    Register with Facebook
                  </ButtonSocial>
                )}
                {(onGoogleRegister ||
                  onFacebookRegister ||
                  onAppleRegister) && <Divider color='dark' />}

                <EditorReveal
                  onSubmit={(value: any) => onEmailRegister(value?.email)}
                  revealLabel='Register with personal email'
                  submitLabel='Register'
                  revealVariant='link'
                  submitVariant='primary'
                  fields={createFieldHelper<{ email: string }>({
                    email: {
                      label: 'Personal email',
                      placeholder: 'personal.email@example.com',
                      isRequired: true,
                      validationRules: [{ type: 'email' }],
                      field: {
                        type: FieldType.Email,
                        autoFocus: true,
                      },
                    },
                  })}
                />
              </ButtonSocialContainer>
            </ButtonContainer>
            <div>
              You can read our privacy policy{' '}
              <Button
                ariaLabel='privacy policy'
                to='https://www.karehero.com/privacy-policy'
                variant='link'
                target='_blank'
                isInline
              >
                here
              </Button>
            </div>
          </Wrapper>
        </StyledContainer>
      }
    />
  );
};

const StyledContainer = styled(Container)`
  background: ${(props) => props.theme.color.cards.yellow.tertiary};
  overflow-y: auto;
  height: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 0 ${(props) => props.theme.spacing[8]};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: ${(props) => props.theme.spacing[32]} 0;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  font: ${(props) => props.theme.typography.heading['5xl']};
  margin: 0;

  ${(props) =>
    props.theme.breakpoint.md(css`
      font: ${props.theme.typography.heading['6xl']};
    `)}
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[48]};
  margin-bottom: ${(props) => props.theme.spacing[64]};
`;

const ButtonSocialContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[12]};
`;

const TopContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: ${(props) => props.theme.spacing[128]};
  background: ${(props) => props.theme.color.cards.yellow.tertiary};
  height: 100%;
`;

const RibbonWrapper = styled.div`
  position: absolute;
  top: 128px;
  ${(props) =>
    props.theme.breakpoint.md(css`
      top: 64px;
    `)}
  left: calc(50% + 48px);
  transform: translateX(-50%) rotate(30deg);
  & > img {
    width: 2600px;
    ${(props) =>
      props.theme.breakpoint.md(css`
        width: 5000px;
      `)}
  }
`;

Register.displayName = 'Register';

export default Register;
